import { LayoutDashboard, Users, UploadCloud, BookOpen, ClipboardCheck, Banknote, Wallet, Bus, Home as HomeIcon, Heart, Store, ShoppingCart, Wifi, Sparkles, FileText, Settings2, ShieldCheck, UserPlus, GraduationCap, LogOut, Crown } from "lucide-react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export type SchoolAdminModule =
  | "cockpit" | "students" | "invitations" | "import" | "academics" | "attendance" | "fees" | "wallets"
  | "transport" | "boarding" | "health" | "merchants" | "procurement" | "internet"
  | "marketplace" | "reports" | "audit" | "settings";

type Counts = Partial<Record<SchoolAdminModule, number>>;

const GROUPS: { label: string; hint?: string; items: { id: SchoolAdminModule; label: string; Icon: any; badge?: keyof Counts }[] }[] = [
  { label: "Cockpit", hint: "Live situation",
    items: [{ id: "cockpit", label: "Situation Room", Icon: LayoutDashboard }] },
  { label: "People", hint: "Roll · guardians · invites",
    items: [
      { id: "students", label: "Students & Guardians", Icon: Users, badge: "students" },
      { id: "invitations", label: "Invitations", Icon: UserPlus },
      { id: "import", label: "Import & migrate", Icon: UploadCloud },
    ]},
  { label: "Academics", hint: "Teaching & learning",
    items: [
      { id: "academics", label: "Timetable & Grades", Icon: BookOpen },
      { id: "attendance", label: "Attendance & Welfare", Icon: ClipboardCheck },
    ]},
  { label: "Treasury", hint: "Ledger-anchored",
    items: [
      { id: "fees", label: "Fees & billing", Icon: Banknote },
      { id: "wallets", label: "Wallet oversight", Icon: Wallet },
      { id: "procurement", label: "Procurement", Icon: ShoppingCart },
    ]},
  { label: "Operations", hint: "Day-to-day rails",
    items: [
      { id: "transport", label: "Transport telematics", Icon: Bus, badge: "transport" },
      { id: "boarding", label: "Boarding", Icon: HomeIcon },
      { id: "health", label: "Health bay", Icon: Heart },
      { id: "merchants", label: "Merchants & POS", Icon: Store, badge: "merchants" },
      { id: "internet", label: "Internet & services", Icon: Wifi },
      { id: "marketplace", label: "Marketplace", Icon: Sparkles },
    ]},
  { label: "Governance", hint: "Regulated · 10-yr retention",
    items: [
      { id: "reports", label: "Reports", Icon: FileText },
      { id: "audit", label: "Audit trail", Icon: ShieldCheck },
      { id: "settings", label: "Settings & RBAC", Icon: Settings2 },
    ]},
];

export function SchoolAdminSidebar({ current, onSelect, schoolName, counts, disabledModules }: {
  current: SchoolAdminModule; onSelect: (m: SchoolAdminModule) => void;
  schoolName?: string; counts?: Counts;
  disabledModules?: string[];
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { user } = useAuth();
  const crest = (schoolName ?? "SO").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

  return (
    <Sidebar collapsible="icon" className="border-r bg-gradient-to-b from-[color-mix(in_oklab,var(--forest)_6%,transparent)] via-background to-background">
      <SidebarHeader className="border-b px-2 py-3">
        <div className="flex items-center gap-2.5">
          <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-md bg-gradient-to-br from-[var(--forest)] to-[color-mix(in_oklab,var(--forest)_60%,black)] text-primary-foreground shadow-md">
            <span className="font-display text-lg leading-none">{crest}</span>
            <span className="absolute -right-0.5 -top-0.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-[var(--ochre)] text-[7px] text-foreground shadow ring-2 ring-background">
              <Crown className="h-2 w-2" />
            </span>
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate font-display text-sm leading-tight">{schoolName ?? "School Admin"}</div>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Console · live
              </div>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-none">
        {GROUPS.map(g => (
          <SidebarGroup key={g.label}>
            {!collapsed && (
              <SidebarGroupLabel className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-muted-foreground/80">
                <span>{g.label}</span>
                {g.hint && <span className="normal-case tracking-normal text-[9px] text-muted-foreground/60">{g.hint}</span>}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {g.items.filter(it => !(disabledModules ?? []).includes(it.id)).map(it => {
                  const active = current === it.id;
                  const count = it.badge ? counts?.[it.badge] : undefined;
                  return (
                    <SidebarMenuItem key={it.id}>
                      <SidebarMenuButton
                        isActive={active}
                        onClick={() => onSelect(it.id)}
                        className={`group cursor-pointer transition ${active ? "!bg-[color-mix(in_oklab,var(--forest)_12%,transparent)] !text-[color-mix(in_oklab,var(--forest)_75%,black)]" : ""}`}
                      >
                        <it.Icon className={`h-4 w-4 ${active ? "text-[var(--forest)]" : ""}`} />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{it.label}</span>
                            {count !== undefined && count > 0 && (
                              <Badge variant="outline" className="ml-auto h-4 px-1.5 text-[9px] font-mono">{count}</Badge>
                            )}
                            {active && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-[var(--ochre)]" />}
                          </>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t px-2 py-2">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-muted text-[11px] font-medium">
              {(user?.email ?? "SA").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium">{user?.email ?? "Admin"}</div>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <GraduationCap className="h-2.5 w-2.5" /> Head admin
              </div>
            </div>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={async () => { await supabase.auth.signOut(); toast.success("Signed out"); }}>
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Button size="icon" variant="ghost" className="mx-auto h-8 w-8" onClick={async () => { await supabase.auth.signOut(); }}>
            <LogOut className="h-3.5 w-3.5" />
          </Button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
